import { Box } from '@material-ui/core';
import { CheckoutSession } from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';

import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import { GetPricingsService } from '@/components/modules/Application/components/PaymentMethod/_shared/services/GetPricingsService';
import { useAutoLoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useAutoLoanPaymentMethodDetailEntity';
import { useDialogSteps } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useDialogSteps';
import { EditAutoPaymentMethodDialogForm } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/EditAutoPaymentMethodDialogForm';
import { useEditFinancingDefaults } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/hooks/useEditFinancingDefaults';
import { financingSummaryToFormFields } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/editFinancingSimulator.map';
import { resolveVehicleAmountRange } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/vehicleAmountRange.map';
import { EditAutoPaymentMethodFormSchema } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/schemas/editAutoPaymentMethodForm.schema';
import { EditAutoPaymentMethodDialogStep } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/EditAutoPaymentMethodDialogStep';
import { EditAutoPaymentMethodEntryMode } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/EditAutoPaymentMethodEntryMode';
import { parseVehicleAmount } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/utils/editVehicleLiveSummaryFormatters';
import { useRestoreVehicleDataFromCheckoutSession } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/hooks/useRestoreVehicleDataFromCheckoutSession';
import { FormWrapper } from '@/components/shared/components/FormWrapper';
import { LoadingFill } from '@/components/shared/components/LoadingFill';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { useTranslation } from '@/components/shared/hooks/useTranslation';

const VEHICLE_ENTRY_STEPS: EditAutoPaymentMethodDialogStep[] = [
  EditAutoPaymentMethodDialogStep.VEHICLE,
  EditAutoPaymentMethodDialogStep.SIMULATOR,
  EditAutoPaymentMethodDialogStep.CHANGES_REVIEW,
];

const FINANCING_ENTRY_STEPS: EditAutoPaymentMethodDialogStep[] = [
  EditAutoPaymentMethodDialogStep.SIMULATOR,
  EditAutoPaymentMethodDialogStep.CHANGES_REVIEW,
];

const pricingsService = GetPricingsService.getInstance();

interface EditAutoPaymentMethodDialogProps {
  entryMode: EditAutoPaymentMethodEntryMode;
  checkoutSession: CheckoutSession;
  opened: boolean;
  setOpened: (opened: boolean) => void;
  refreshCheckoutSessionAndPaymentMethodEntities: () => Promise<void>;
}

export const EditAutoPaymentMethodDialog = ({
  entryMode,
  checkoutSession,
  opened,
  setOpened,
  refreshCheckoutSessionAndPaymentMethodEntities,
}: EditAutoPaymentMethodDialogProps) => {
  const autoLoanEntity = useAutoLoanPaymentMethodDetailEntity();
  const {
    cart,
    isReady: isVehicleReady,
    loading: vehicleLoading,
    resetApplicationContext,
  } = useRestoreVehicleDataFromCheckoutSession(checkoutSession, opened);
  const { financingSummary } = useEditFinancingDefaults({
    checkoutSession,
    loan: autoLoanEntity?.loan,
  });
  const { getTextIdTranslation } = useTranslation();

  const steps = entryMode === 'vehicle' ? VEHICLE_ENTRY_STEPS : FINANCING_ENTRY_STEPS;
  const initialStep =
    entryMode === 'financing' ? EditAutoPaymentMethodDialogStep.SIMULATOR : EditAutoPaymentMethodDialogStep.VEHICLE;

  const { step, onNext, goToStep, reset } = useDialogSteps({
    steps,
    initialStep,
    onComplete: () => setOpened(false),
    onCancel: () => setOpened(false),
  });

  const initialFormItem = useMemo(
    () => cart?.items[0]?.toFormItem() as AutomobileFormItem | undefined,
    [cart?.items[0]],
  );
  const loading = vehicleLoading;
  const isReady = isVehicleReady && !!initialFormItem;

  const vehicleAmountRange = useMemo(() => {
    const downPayment = financingSummary.downPaymentAmount ?? 0;
    const discount = financingSummary.discountAmount ?? 0;
    const vehicleAmountFromCart = parseVehicleAmount(initialFormItem?.price);
    const fallbackPrincipal =
      financingSummary.principalAmount ??
      (vehicleAmountFromCart !== null ? vehicleAmountFromCart - downPayment - discount : null);

    return resolveVehicleAmountRange({
      pricings: pricingsService.pricings,
      loan: autoLoanEntity?.loan,
      downPaymentAmount: financingSummary.downPaymentAmount,
      discountAmount: financingSummary.discountAmount,
      fallbackPrincipal,
    });
  }, [
    autoLoanEntity?.loan,
    financingSummary.downPaymentAmount,
    financingSummary.discountAmount,
    financingSummary.principalAmount,
    initialFormItem?.price,
  ]);

  const isWideStep =
    step === EditAutoPaymentMethodDialogStep.VEHICLE || step === EditAutoPaymentMethodDialogStep.SIMULATOR;

  const onClose = () => {
    setOpened(false);
  };

  const handleExited = () => {
    reset();
    resetApplicationContext();
  };

  return (
    <NemuruDialog
      opened={opened}
      setOpened={setOpened}
      hideButtonsWrapper
      dialogProps={{
        maxWidth: isWideStep ? 'lg' : 'sm',
        fullWidth: true,
        TransitionProps: { onExited: handleExited },
        disableBackdropClick: true,
      }}
      onClose={onClose}
    >
      <Box position="relative" minHeight={loading ? 200 : undefined}>
        {loading && <LoadingFill open />}
        {isReady && initialFormItem ? (
          <FormWrapper
            formSchema={() =>
              EditAutoPaymentMethodFormSchema(
                getTextIdTranslation,
                vehicleAmountRange,
                checkoutSession.totalPrice.currency,
              )
            }
            defaultValues={{
              item: initialFormItem as AutomobileFormItem,
              financing: financingSummaryToFormFields(financingSummary),
            }}
          >
            <EditAutoPaymentMethodDialogForm
              entryMode={entryMode}
              step={step}
              onNext={onNext}
              goToStep={goToStep}
              checkoutSessionId={checkoutSession.id}
              currency={checkoutSession.totalPrice.currency}
              initialFormItem={initialFormItem as AutomobileFormItem}
              initialFinancing={financingSummary}
              vehicleAmountRange={vehicleAmountRange}
              onClose={onClose}
              refreshCheckoutSessionAndPaymentMethodEntities={refreshCheckoutSessionAndPaymentMethodEntities}
            />
          </FormWrapper>
        ) : null}
      </Box>
    </NemuruDialog>
  );
};
